import React, { createContext, useReducer, ReactNode } from 'react';
import { NotificationPreference } from '@hooks/notifications';

type State = {
  notificationPreference: NotificationPreference | undefined;
  hasEmailConsent: boolean;
  hasPushConsent: boolean;
  isSubmitting: boolean;
  isPending: boolean;
  error: string;
  isModalOpen: boolean;
  modalType: 'no-updates' | 'email-only';
};

type Action =
  | { type: 'SET_PREFERENCE'; payload: NotificationPreference }
  | { type: 'SET_EMAIL_CONSENT'; payload: boolean }
  | { type: 'SET_PUSH_CONSENT'; payload: boolean }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'SUBMIT_ERROR'; payload: string }
  | { type: 'SHOW_MODAL'; payload: 'no-updates' | 'email-only' }
  | { type: 'HIDE_MODAL' };

const initialState: State = {
  notificationPreference: undefined,
  hasEmailConsent: false,
  hasPushConsent: false,
  isSubmitting: false,
  isPending: false,
  error: '',
  isModalOpen: false,
  modalType: 'no-updates'
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_PREFERENCE':
      return { ...state, notificationPreference: action.payload };
    case 'SET_EMAIL_CONSENT':
      return { ...state, hasEmailConsent: action.payload };
    case 'SET_PUSH_CONSENT':
      return { ...state, hasPushConsent: action.payload };
    case 'SUBMIT_START':
      return { ...state, isSubmitting: true, error: '' };
    case 'SUBMIT_SUCCESS':
      return { ...state, isSubmitting: false, isPending: true };
    case 'SUBMIT_ERROR':
      return { ...state, isSubmitting: false, error: action.payload };
    case 'SHOW_MODAL':
      return { ...state, isModalOpen: true, modalType: action.payload };
    case 'HIDE_MODAL':
      return { ...state, isModalOpen: false };
    default:
      return state;
  }
};

const Context = createContext<
  | {
      state: State;
      dispatch: React.Dispatch<Action>;
    }
  | undefined
>(undefined);

const ContextProvider: React.FC<{
  children: ReactNode;
  initialPreferences?: Record<string, any>;
}> = ({ children, initialPreferences }) => {
  const mergedInitialState = initialPreferences
    ? {
        ...initialState,
        hasEmailConsent: initialPreferences.hasEmailConsent || false,
        hasPushConsent: initialPreferences.hasPushConsent || false,
        notificationPreference:
          initialPreferences.notificationPreference || undefined
      }
    : initialState;

  const [state, dispatch] = useReducer(reducer, mergedInitialState);

  return (
    <Context.Provider value={{ state, dispatch }}>{children}</Context.Provider>
  );
};

export default ContextProvider;
